import { useState, useEffect, useCallback } from 'react';
import { Modal } from '../ui/Modal';
import { Plus, Trash2, Eye, EyeOff, GripVertical, AlertCircle, ChevronDown, ChevronUp, X } from 'lucide-react';
import '../../components/ui/datatable.css';

/* ═══════════════════════════════════════════════════════════════
   ListManagerDialog — Gerenciador genérico de listas configuráveis
   Uso:
     <ListManagerDialog
       open={showConfig}
       onClose={() => setShowConfig(false)}
       title="Etapas do Funil"
       items={etapas}
       onSave={handleSave}
       showColor
       showVisibility
       showParams
       linkedCounts={{ prospeccao: 5, contato: 2 }}
     />
   ═══════════════════════════════════════════════════════════════ */

export interface ListItemParam {
  name: string;
  value: string;
}

export interface ListItem {
  id: string;
  label: string;
  color?: string;
  ordem: number;
  visivel: boolean;
  params?: ListItemParam[];
}

export interface ListManagerDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  items: ListItem[];
  onSave: (items: ListItem[]) => void;
  nameLabel?: string;
  valueLabel?: string;
  showColor?: boolean;
  showVisibility?: boolean;
  /** Permite adicionar parâmetros customizados (name/value) a cada item */
  showParams?: boolean;
  /** Mapa de { id: contagem } para validar remoção */
  linkedCounts?: Record<string, number>;
  saving?: boolean;
}

export function ListManagerDialog({
  open,
  onClose,
  title,
  items: initialItems,
  onSave,
  nameLabel = 'Nome',
  valueLabel = 'ID (valor)',
  showColor = false,
  showVisibility = false,
  showParams = false,
  linkedCounts = {},
  saving = false,
}: ListManagerDialogProps) {
  const [items, setItems] = useState<ListItem[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  /** Qual item tem o painel de params expandido */
  const [expandedParams, setExpandedParams] = useState<number | null>(null);

  // Sync com props quando abre
  useEffect(() => {
    if (open) {
      setItems(initialItems.map((it, i) => ({ ...it, ordem: it.ordem ?? i + 1, params: it.params || [] })));
      setErrors({});
      setExpandedParams(null);
    }
  }, [open, initialItems]);

  const validate = useCallback((list: ListItem[]): boolean => {
    const newErrors: Record<string, string> = {};
    const ids = new Set<string>();
    const labels = new Set<string>();

    for (let i = 0; i < list.length; i++) {
      const it = list[i];
      if (!it.id.trim()) newErrors[`${i}-id`] = 'ID obrigatório';
      if (!it.label.trim()) newErrors[`${i}-label`] = 'Nome obrigatório';

      const normalId = it.id.trim().toLowerCase();
      const normalLabel = it.label.trim().toLowerCase();

      if (ids.has(normalId)) newErrors[`${i}-id`] = 'ID duplicado';
      if (labels.has(normalLabel)) newErrors[`${i}-label`] = 'Nome duplicado';

      ids.add(normalId);
      labels.add(normalLabel);
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, []);

  const addItem = () => {
    const maxOrdem = items.reduce((m, it) => Math.max(m, it.ordem), 0);
    setItems([...items, { id: '', label: '', color: '#6366f1', ordem: maxOrdem + 1, visivel: true, params: [] }]);
  };

  const updateItem = (index: number, field: keyof ListItem, value: any) => {
    setItems(prev => prev.map((it, i) => i === index ? { ...it, [field]: value } : it));
    // Auto-gerar ID a partir do label se ID está vazio
    if (field === 'label' && !items[index].id) {
      const autoId = (value as string).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
      setItems(prev => prev.map((it, i) => i === index ? { ...it, id: autoId } : it));
    }
  };

  const removeItem = (index: number) => {
    const item = items[index];
    if (linkedCounts[item.id] && linkedCounts[item.id] > 0) return; // bloqueado
    setItems(prev => prev.filter((_, i) => i !== index));
    if (expandedParams === index) setExpandedParams(null);
  };

  const toggleVisibility = (index: number) => {
    setItems(prev => prev.map((it, i) => i === index ? { ...it, visivel: !it.visivel } : it));
  };

  // ── Params management ──
  const addParam = (itemIdx: number) => {
    setItems(prev => prev.map((it, i) =>
      i === itemIdx ? { ...it, params: [...(it.params || []), { name: '', value: '' }] } : it
    ));
  };

  const updateParam = (itemIdx: number, paramIdx: number, field: 'name' | 'value', val: string) => {
    setItems(prev => prev.map((it, i) => {
      if (i !== itemIdx) return it;
      const params = [...(it.params || [])];
      params[paramIdx] = { ...params[paramIdx], [field]: val };
      return { ...it, params };
    }));
  };

  const removeParam = (itemIdx: number, paramIdx: number) => {
    setItems(prev => prev.map((it, i) => {
      if (i !== itemIdx) return it;
      const params = (it.params || []).filter((_, pi) => pi !== paramIdx);
      return { ...it, params };
    }));
  };

  const toggleParams = (index: number) => {
    setExpandedParams(prev => prev === index ? null : index);
  };

  const handleSave = () => {
    if (!validate(items)) return;
    // Re-numerar ordens
    const sorted = items.map((it, i) => ({ ...it, ordem: i + 1 }));
    onSave(sorted);
  };

  // Drag reorder
  const handleDragStart = (idx: number) => setDragIdx(idx);
  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    setItems(prev => {
      const next = [...prev];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(idx, 0, moved);
      return next;
    });
    setDragIdx(idx);
    // Ajustar expandedParams se necessário
    if (expandedParams !== null) {
      if (expandedParams === dragIdx) setExpandedParams(idx);
      else if (expandedParams === idx) setExpandedParams(dragIdx);
    }
  };
  const handleDragEnd = () => setDragIdx(null);

  const canDelete = (item: ListItem) => !linkedCounts[item.id] || linkedCounts[item.id] === 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title=""
      size="lg"
      contentClassName="umu-wizard-modal"
      footer={
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn-cancel" onClick={onClose}>Cancelar</button>
          <button className="btn-save" onClick={handleSave} disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      }
    >
      {/* Banner premium (padrão umu-wizard-modal) */}
      <div style={{
        background: 'linear-gradient(135deg, #312e81 0%, #3730a3 40%, #4f46e5 100%)',
        borderRadius: '12px 12px 0 0', padding: '20px 24px 16px', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -40, right: -40, width: 140, height: 140, borderRadius: '50%', background: 'rgba(255,255,255,.05)' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <h2 style={{ color: 'white', fontSize: '1.1rem', fontWeight: 800, margin: '0 0 4px' }}>
            ⚙️ {title}
          </h2>
          <p style={{ color: 'rgba(255,255,255,.55)', fontSize: '.78rem', margin: 0 }}>
            Gerencie os itens da lista. Arraste para reordenar.
          </p>
        </div>
      </div>

      <div style={{ padding: '16px 24px 20px' }}>

      {/* Header da lista */}
      <div className="lm-header">
        <div className="lm-col-grip" />
        {showColor && <div className="lm-col-color">Cor</div>}
        <div className="lm-col-label">{nameLabel}</div>
        <div className="lm-col-id">{valueLabel}</div>
        {showVisibility && <div className="lm-col-vis">Visível</div>}
        <div className="lm-col-actions">Ações</div>
      </div>

      {/* Lista de itens */}
      <div className="lm-list custom-scroll" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
        {items.map((item, i) => {
          const linked = linkedCounts[item.id] || 0;
          const deletable = canDelete(item);
          const paramCount = (item.params || []).length;
          const isExpanded = expandedParams === i;
          return (
            <div key={i} className="lm-row-wrap">
              <div
                className={`lm-row${!item.visivel ? ' lm-row-hidden' : ''}${dragIdx === i ? ' lm-row-dragging' : ''}`}
                draggable
                onDragStart={() => handleDragStart(i)}
                onDragOver={(e) => handleDragOver(e, i)}
                onDragEnd={handleDragEnd}
              >
                <div className="lm-col-grip"><GripVertical size={14} /></div>

                {showColor && (
                  <div className="lm-col-color">
                    <input
                      type="color"
                      value={item.color || '#6366f1'}
                      onChange={(e) => updateItem(i, 'color', e.target.value)}
                      className="lm-color-input"
                    />
                  </div>
                )}

                <div className="lm-col-label">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input
                      className={`form-input lm-input${errors[`${i}-label`] ? ' form-input--error' : ''}`}
                      value={item.label}
                      onChange={(e) => updateItem(i, 'label', e.target.value)}
                      placeholder="Ex: Nova Etapa"
                      style={{ flex: 1 }}
                    />
                    {showParams && (
                      <button
                        className={`lm-param-toggle${isExpanded ? ' lm-param-toggle-active' : ''}${paramCount > 0 ? ' lm-param-has' : ''}`}
                        onClick={() => toggleParams(i)}
                        title={paramCount > 0 ? `${paramCount} parâmetro(s) — clique para editar` : 'Adicionar parâmetros'}
                      >
                        {paramCount > 0 && <span className="lm-param-count">{paramCount}</span>}
                        {isExpanded ? <ChevronUp size={12} /> : <Plus size={12} />}
                      </button>
                    )}
                  </div>
                  {errors[`${i}-label`] && <span className="form-error-text">{errors[`${i}-label`]}</span>}
                </div>

                <div className="lm-col-id">
                  <input
                    className={`form-input lm-input${errors[`${i}-id`] ? ' form-input--error' : ''}`}
                    value={item.id}
                    onChange={(e) => updateItem(i, 'id', e.target.value)}
                    placeholder="id_unico"
                  />
                  {errors[`${i}-id`] && <span className="form-error-text">{errors[`${i}-id`]}</span>}
                </div>

                {showVisibility && (
                  <div className="lm-col-vis">
                    <button
                      className={`lm-vis-btn${item.visivel ? ' lm-vis-on' : ''}`}
                      onClick={() => toggleVisibility(i)}
                      title={item.visivel ? 'Ocultar' : 'Exibir'}
                    >
                      {item.visivel ? <Eye size={15} /> : <EyeOff size={15} />}
                    </button>
                  </div>
                )}

                <div className="lm-col-actions">
                  {deletable ? (
                    <button className="lm-delete-btn" onClick={() => removeItem(i)} title="Remover">
                      <Trash2 size={14} />
                    </button>
                  ) : (
                    <button className="lm-delete-btn lm-delete-blocked" title={`${linked} itens vinculados`} disabled>
                      <Trash2 size={14} />
                    </button>
                  )}
                  {linked > 0 && <span className="lm-linked-badge">{linked}</span>}
                </div>
              </div>

              {/* ── Painel de parâmetros expandido inline ── */}
              {showParams && isExpanded && (
                <div className="lm-params-panel">
                  <div className="lm-params-header">
                    <span className="lm-params-title">Parâmetros de "{item.label || 'Item'}"</span>
                  </div>
                  {(item.params || []).length > 0 && (
                    <div className="lm-params-list">
                      {(item.params || []).map((p, pi) => (
                        <div key={pi} className="lm-param-row">
                          <input
                            className="form-input lm-input lm-param-input"
                            value={p.name}
                            onChange={e => updateParam(i, pi, 'name', e.target.value)}
                            placeholder="Nome"
                          />
                          <input
                            className="form-input lm-input lm-param-input"
                            value={p.value}
                            onChange={e => updateParam(i, pi, 'value', e.target.value)}
                            placeholder="Valor"
                          />
                          <button
                            className="lm-param-remove"
                            onClick={() => removeParam(i, pi)}
                            title="Remover parâmetro"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <button className="lm-param-add" onClick={() => addParam(i)}>
                    <Plus size={12} /> Adicionar Parâmetro
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Botão adicionar */}
      <button className="lm-add-btn" onClick={addItem}>
        <Plus size={14} /> Adicionar Item
      </button>
      </div>
    </Modal>
  );
}
